import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FormsModule} from '@angular/forms';

import {MessageService} from 'primeng/api';
import {ConfirmationService} from 'primeng/api';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
registerLocaleData(localeFr);

import { ApiService } from './api.service';
import { IsLoggedGuard, IsProGuard } from './is-logged.guard';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import {InputTextModule} from 'primeng/inputtext';
import {CheckboxModule} from 'primeng/checkbox';
import {ButtonModule} from 'primeng/button';
import {MessagesModule} from 'primeng/messages';
import {MessageModule} from 'primeng/message';
import {ToastModule} from 'primeng/toast';
import { HomeComponent } from './home/home.component';
import { HeaderComponent } from './components/header/header.component';
import {ToolbarModule} from 'primeng/toolbar';
import {SplitButtonModule} from 'primeng/splitbutton';
import {MenubarModule} from 'primeng/menubar';
import {TooltipModule} from 'primeng/tooltip';
import { PagesComponent } from './pages/pages.component';
import {ProgressBarModule} from 'primeng/progressbar';
import { GamesComponent } from './games/games.component';
import { BlocGameComponent } from './components/bloc-game/bloc-game.component';
import {StyleClassModule} from 'primeng/styleclass';
import {ListboxModule} from 'primeng/listbox';
import {AccordionModule} from 'primeng/accordion';
import {ScrollPanelModule} from 'primeng/scrollpanel';
import {SkeletonModule} from 'primeng/skeleton';
import {MenuModule} from 'primeng/menu';
import {DataViewModule} from 'primeng/dataview';
import {DropdownModule} from 'primeng/dropdown';
import {SelectButtonModule} from 'primeng/selectbutton';
import {TableModule} from 'primeng/table';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import { CartComponent } from './cart/cart.component';
import {InputNumberModule} from 'primeng/inputnumber';
import {BadgeModule} from 'primeng/badge';
import { TagModule } from 'primeng/tag';
import {OverlayPanelModule} from 'primeng/overlaypanel';
import {BlockUIModule} from 'primeng/blockui';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {DialogModule} from 'primeng/dialog';
import {RadioButtonModule} from 'primeng/radiobutton';
import { OrderModule } from 'ngx-order-pipe';
import { GameComponent } from './game/game.component';
import {ImageModule} from 'primeng/image';
import {CardModule} from 'primeng/card';
import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {DividerModule} from 'primeng/divider';
import {CarouselModule} from 'primeng/carousel';
import {ScrollTopModule} from 'primeng/scrolltop';
import {TrimPipe, SafeUrlPipe, EllipsisPipe, SafeHtmlPipe} from './pipes';
import { FooterComponent } from './components/footer/footer.component';
import { MyOrdersComponent } from './my-orders/my-orders.component';
import { MyAccountComponent } from './my-account/my-account.component';
import { PeriodComponent } from './components/period/period.component';
import {CalendarModule} from 'primeng/calendar';
import {PasswordModule} from 'primeng/password';
import {InputMaskModule} from 'primeng/inputmask';
import {PanelModule} from 'primeng/panel';
import {KeyFilterModule} from 'primeng/keyfilter';
import {GalleriaModule} from 'primeng/galleria';
import { ChipModule } from 'primeng/chip';
import {SliderModule} from 'primeng/slider';
import {TriStateCheckboxModule} from 'primeng/tristatecheckbox';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

import { OrderComponent } from './order/order.component';
import { OffresComponent } from './offres/offres.component';
import { FastOrderComponent } from './fast-order/fast-order.component';
import {FileUploadModule} from 'primeng/fileupload';
import {BreadcrumbModule} from 'primeng/breadcrumb';
import { NewslettersComponent } from './newsletters/newsletters.component';
import { RdvComponent } from './rdv/rdv.component';
import {SidebarModule} from 'primeng/sidebar';
import {AutoCompleteModule} from 'primeng/autocomplete';
import { EditorsComponent } from './editors/editors.component';
import { LandingComponent } from './landing/landing.component';
import { StoresComponent } from './stores/stores.component';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@asymmetrik/ngx-leaflet-markercluster';
import { SavComponent } from './sav/sav.component';
import { NewsComponent } from './news/news.component';
import { PreordersComponent } from './preorders/preorders.component';
import { ContactComponent } from './contact/contact.component';
import { AdminOrderComponent } from './admin/admin-order/admin-order.component';
import { CreditRequestsComponent } from './admin/admin-order/credit-requests/credit-requests.component';
import { StepsModule } from 'primeng/steps';
import { BdcComponent } from './admin/admin-order/bdc/bdc.component';
import { AdminComponent } from './admin/admin.component';
import { TabViewModule } from 'primeng/tabview';
import { SavedOrdersComponent } from './my-orders/saved-orders/saved-orders.component';
import { QuotesComponent } from './admin/admin-order/quotes/quotes.component';
import { AngularResizeEventModule } from 'angular-resize-event';
import { AdminProComponent } from './admin/admin-pro/admin-pro.component';
import { NetworksComponent } from './admin/admin-pro/networks/networks.component';
import { ConditionsComponent } from './admin/admin-pro/conditions/conditions.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { AdminGamesComponent } from './admin/admin-pro/admin-games/admin-games.component';
import { OpenComponent } from './admin/admin-pro/open/open.component';
import { CatalogueComponent } from './catalogue/catalogue.component';
import { FlipbookComponent } from './catalogue/flipbook/flipbook.component';
import { AddressComponent } from './my-account/address/address.component';
import { HomeExportComponent } from './home/home-export/home-export.component';
import { EventsComponent } from './events/events.component';
import { AdminCatalogueComponent } from './admin/admin-pro/admin-catalogue/admin-catalogue.component';
import { BilanRseComponent } from './catalogue/bilan-rse/bilan-rse.component';


@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    HomeComponent,
    HeaderComponent,
    PagesComponent,
    GamesComponent,
    BlocGameComponent,
    CartComponent,
    GameComponent,
    TrimPipe, SafeUrlPipe,EllipsisPipe,SafeHtmlPipe,
    FooterComponent,
    MyOrdersComponent,
    MyAccountComponent,
    PeriodComponent,
    OrderComponent,
    OffresComponent,
    FastOrderComponent,
    NewslettersComponent,
    RdvComponent,
    EditorsComponent,
    LandingComponent,
    StoresComponent,
    SavComponent,
    NewsComponent,
    PreordersComponent,
    ContactComponent,
    AdminOrderComponent,
    CreditRequestsComponent,
    BdcComponent,
    AdminComponent,
    SavedOrdersComponent,
    QuotesComponent,
    AdminProComponent,
    NetworksComponent,
    ConditionsComponent,
    AdminGamesComponent,
    OpenComponent,
    CatalogueComponent,
    FlipbookComponent,
    AddressComponent,
    HomeExportComponent,
    EventsComponent,
    AdminCatalogueComponent,
    BilanRseComponent
    ],
    imports: [
    BrowserModule,
    FormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    BrowserAnimationsModule,
    AppRoutingModule,
    InputTextModule,
    HttpClientModule,
    CheckboxModule,
    ButtonModule,
    MessagesModule,
    MessageModule,
    ToastModule,
    ToolbarModule,
    SplitButtonModule,
    MenubarModule,
    TooltipModule,
    ProgressBarModule,
    StyleClassModule,
    ListboxModule,
    AccordionModule,
    ScrollPanelModule,
    SkeletonModule,
    MenuModule,
    DataViewModule,
    DropdownModule,
    SelectButtonModule,
    TableModule,
    ProgressSpinnerModule,
    InputNumberModule,
    BadgeModule,
    TagModule,
    OverlayPanelModule,
    BlockUIModule,
    ConfirmDialogModule,
    DialogModule,
    RadioButtonModule,
    OrderModule,
    ImageModule,
    CardModule,
    DividerModule,
    CarouselModule,
    ScrollTopModule,
    CalendarModule,
    PasswordModule,
    InputMaskModule,
    PanelModule,
    KeyFilterModule,
    GalleriaModule,
    ChipModule,
    SliderModule,
    FileUploadModule,
    TriStateCheckboxModule,
    BreadcrumbModule,
    SidebarModule,
    AutoCompleteModule,
    LeafletModule,
    LeafletMarkerClusterModule,
    StepsModule,
    TabViewModule,
    AngularResizeEventModule,
    MultiSelectModule
  ],
    providers: [
    ApiService,
    MessageService,
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    ConfirmationService,
    IsLoggedGuard,IsProGuard
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
